import { IdCard, Building2, CalendarClock, Palette } from 'lucide-react';
import { SUPPORTED } from '../../i18n/config.js';

// پروفایل — the section set and the identity form's edit model, shared by both
// surfaces so they can never disagree about what "dirty" means or which fields
// go on the wire.
//
// `Icon` is read only by the mobile `SectionNav`; the desktop `SegmentedControl`
// is label-only.

export const PROFILE_SECTIONS = (t) => [
  { value: 'identity', label: t('profile.sections.identity'), Icon: IdCard },
  { value: 'clinics', label: t('profile.sections.clinics'), Icon: Building2 },
  { value: 'presence', label: t('profile.sections.presence'), Icon: CalendarClock },
  { value: 'appearance', label: t('profile.sections.appearance'), Icon: Palette },
];

/**
 * The language switcher's options, one per supported locale, each labelled with
 * its endonym. From the registry, so the switcher can never offer a language the
 * app cannot render, nor miss one it can — on either surface.
 */
export const languageOptions = (t) =>
  Object.keys(SUPPORTED).map((id) => ({ value: id, label: t(`profile.language.${id}`) }));

export const PROFILE_SECTION_IDS = ['identity', 'clinics', 'presence', 'appearance'];
export const DEFAULT_PROFILE_SECTION = 'identity';

/** The identity fields the form owns. Everything else on `me` is read-only. */
export const FIELDS = ['fullName', 'medicalCode', 'bio'];

/**
 * کد نظام پزشکی is stated once, at sign-up, and is locked afterwards.
 *
 * It is not a profile field: it is this account's identity in
 * سازمان نظام پزشکی, and what the name on every prescription already
 * signed was issued against — a dentist who could re-type it could point their
 * account at somebody else's registration. The BFF ignores the field once one
 * is stored; this says the same thing where the dentist can see it, so the form
 * never accepts typing that will be silently dropped on save.
 *
 * Still open for an account that holds NONE, from before it was asked for.
 * That is a first statement, not a change.
 */
export const isMedicalCodeLocked = (saved) => Boolean((saved?.medicalCode || '').trim());

export const snapshot = (me) => Object.fromEntries(FIELDS.map((k) => [k, me?.[k] ?? '']));

/**
 * ONLY the keys that actually changed.
 *
 * The service treats an absent field as "leave alone" and a present empty one
 * as "blank it", so sending the whole form would erase a bio the dentist never
 * touched.
 */
export const changedOnly = (form, saved) =>
  Object.fromEntries(
    FIELDS.filter((k) => !(k === 'medicalCode' && isMedicalCodeLocked(saved)))
      .filter((k) => (form[k] || '').trim() !== (saved[k] || '').trim())
      .map((k) => [k, (form[k] || '').trim()]),
  );

/** Is there anything to commit? The SaveBar renders only while this is true. */
export const isDirty = (form, saved) =>
  Object.keys(changedOnly(form, saved)).length > 0;

export default PROFILE_SECTIONS;
